import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { generateRandomColor } from '../../shared/colorUtils';
import { useSortPreference } from '../../shared/useSortPreference';
import { usePermissions } from '../../shared/permissions';
import { useConfirm } from '../../shared/ConfirmContext';
import ManagementSearchToolbar from './components/ManagementSearchToolbar';
import TypeListTable from './components/TypeListTable';
import TypeFormModal from './components/TypeFormModal';

export interface ProductType {
  id: number;
  name: string;
  description?: string | null;
  color?: string | null;
}

export interface ProductTypeFormData {
  name: string;
  description: string;
  color: string;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

type ModalMode = 'add' | 'edit' | null;
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE = 50;
const INITIAL_FORM_DATA: ProductTypeFormData = { name: '', description: '', color: '#cccccc' };

const ProductTypeManagement: React.FC = () => {
  const { warehouseId } = useParams<{ warehouseId?: string }>();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [sortField, setSortField] = useSortPreference<string>('product-type-management-field', 'name');
  const [sortDir, setSortDir] = useSortPreference<SortDirection>('product-type-management-dir', 'asc');
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedType, setSelectedType] = useState<ProductType | null>(null);
  const [formData, setFormData] = useState<ProductTypeFormData>(INITIAL_FORM_DATA);

  const tableRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const ordering = sortDir === 'asc' ? sortField : `-${sortField}`;
  const queryKey = ['product-types', warehouseId, debouncedSearch, ordering];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: loading,
    isError,
  } = useInfiniteQuery<PaginatedResponse<ProductType>>({
    queryKey,
    queryFn: ({ pageParam = 1 }) => {
      const params = new URLSearchParams({ page: String(pageParam), page_size: String(PAGE_SIZE) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      params.set('ordering', ordering);
      return api.getRaw(`warehouses/api/product-types/?${params}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const results = Array.isArray(lastPage?.results) ? lastPage.results : [];
      return results.length === PAGE_SIZE ? allPages.length + 1 : undefined;
    },
    staleTime: 30_000,
  });

  const types = data?.pages.flatMap((page) =>
    Array.isArray(page?.results) ? page.results : []
  ) ?? [];
  const totalCount = data?.pages[0]?.count ?? null;
  const loadingMore = isFetchingNextPage;
  const hasMore = !!hasNextPage;
  const error = isError ? 'Failed to load product types. Please try again later.' : mutationError;

  // IntersectionObserver — triggers fetchNextPage when sentinel scrolls into view
  useEffect(() => {
    if (loading) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: tableRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSort = (field: string) => {
    const newDir: SortDirection = sortField === field && sortDir === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDir(newDir);
  };

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: ['product-types', warehouseId] });

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.producttype') invalidateList();
  });

  const handleFieldChange = <K extends keyof ProductTypeFormData>(field: K, value: ProductTypeFormData[K]) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const resetModalState = useCallback(() => {
    setModalMode(null);
    setSelectedType(null);
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const addMutation = useMutation({
    mutationFn: () => api.post('warehouses/api/product-types/', formData),
    onSuccess: () => {
      resetModalState();
      invalidateList();
    },
    onError: () => setMutationError(t('warehouses.productTypes.addError')),
  });

  const editMutation = useMutation({
    mutationFn: () => {
      if (!selectedType) throw new Error('No product type selected for edit');
      return api.put(`warehouses/api/product-types/${selectedType.id}/`, formData);
    },
    onSuccess: () => {
      resetModalState();
      invalidateList();
    },
    onError: () => setMutationError(t('warehouses.productTypes.editError')),
  });

  const deleteMutation = useMutation({
    mutationFn: (typeId: number) => api.delete(`warehouses/api/product-types/${typeId}/`),
    onSuccess: () => invalidateList(),
    onError: () => setMutationError(t('warehouses.productTypes.deleteError')),
  });

  const handleAddType = (e: React.FormEvent) => {
    e.preventDefault();
    addMutation.mutate();
  };

  const handleEditType = (e: React.FormEvent) => {
    e.preventDefault();
    editMutation.mutate();
  };

  const handleSubmitType = (e: React.FormEvent) => {
    if (modalMode === 'edit') {
      handleEditType(e);
      return;
    }
    handleAddType(e);
  };

  const handleDeleteType = async (typeId: number) => {
    if (!(await confirm(t('common.confirm')))) return;
    deleteMutation.mutate(typeId);
  };

  const openEditModal = (type: ProductType) => {
    setSelectedType(type);
    setFormData({ name: type.name, description: type.description || '', color: type.color || '#cccccc' });
    setModalMode('edit');
  };

  const backHref = warehouseId ? `/warehouses/stock/${warehouseId}` : '/warehouses/products';
  const openAddModal = () => {
    setSelectedType(null);
    setFormData(INITIAL_FORM_DATA);
    setModalMode('add');
  };
  const submitting = addMutation.isPending || editMutation.isPending;

  return (
    <PageShell
      pretitle={t('warehouses.title')}
      title={t('warehouses.productTypes.title', 'Typy produktů')}
      description={t('warehouses.productTypes.subtitle', 'Správa typů produktů v katalógu.')}
      actions={
        <>
          <Link to={backHref} className="btn btn-outline-secondary">
            <i className="ti ti-arrow-left me-2"></i>
            {t('common.back')}
          </Link>
          {canManage && (
            <button className="btn btn-primary" onClick={openAddModal}>
              <i className="ti ti-plus me-2"></i>
              {t('warehouses.productTypes.add', 'Přidat typ')}
            </button>
          )}
        </>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}

      {loading && types.length === 0 ? (
        <PageSection>
          <CenteredLoader />
        </PageSection>
      ) : (
        <PageSection
          title={t('warehouses.productTypes.title', 'Typy produktů')}
          description={t('warehouses.productTypes.sectionDescription', 'Vyhledávání a správa definic typů produktů.')}
          bodyClassName="p-0"
        >
          <ManagementSearchToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            totalCount={totalCount}
            visibleCount={types.length}
            entityLabel={t('warehouses.productTypes.title', 'Typy produktů').toLowerCase()}
            placeholder={`${t('common.search')}...`}
          />

          {types.length === 0 ? (
            <EmptyState
              className="py-5"
              icon={<i className="ti ti-box app-empty-state__icon-glyph" aria-hidden="true" />}
              title={t('warehouses.productTypes.empty', 'Žádné typy produktů')}
              message={searchQuery ? t('warehouses.productTypes.emptySearch') : t('warehouses.productTypes.emptyMessage')}
              action={canManage ? (
                <button className="btn btn-primary" onClick={openAddModal}>
                  <i className="ti ti-plus me-2"></i>
                  {t('warehouses.productTypes.add', 'Přidat typ')}
                </button>
              ) : null}
            />
          ) : (
            <TypeListTable
              types={types}
              sortField={sortField}
              sortDir={sortDir}
              onSort={handleSort}
              canManage={canManage}
              onEdit={openEditModal}
              onDelete={handleDeleteType}
              loadingMore={loadingMore}
              tableRef={tableRef}
              sentinelRef={sentinelRef}
              endMessage={!hasMore && !loadingMore && types.length >= PAGE_SIZE
                ? `${types.length} ${t('warehouses.productTypes.title', 'Typy produktů').toLowerCase()} ${t('projects.items.endOfResults') || 'načteno'}`
                : undefined}
              t={t}
            />
          )}
        </PageSection>
      )}

      <TypeFormModal
        show={modalMode !== null}
        editing={modalMode === 'edit'}
        formData={formData}
        submitting={submitting}
        onClose={resetModalState}
        onSubmit={handleSubmitType}
        onFieldChange={handleFieldChange}
        onGenerateColor={() => setFormData((current) => ({ ...current, color: generateRandomColor() }))}
      />
    </PageShell>
  );
};

export default ProductTypeManagement;
